import { Alert, AlertType, Button } from '@design';
import { mailTo } from '@helpers';
import { ActionLink } from '@terminal/components/link/ActionLink';
import { config } from '@terminal/config';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ScenarioConditionBuilder } from '../scenario-condition-builder/ScenarioConditionBuilder';
import { useScenarioAssistant } from '../use-scenario-assistant/useScenarioAssistant';
import { validateConditions } from '../validate-condition/validateCondition';
import styles from './ConditionsView.module.scss';

export interface ConditionsViewProps {
  editFilterKey?: string;
  handleClose: () => void;
  handleEdit: () => void;
}

export const ConditionsView = ({
  editFilterKey,
  handleClose,
  handleEdit,
}: ConditionsViewProps) => {
  const { t } = useTranslation(['screener', 'common']);
  const supportEmail = mailTo(config.supportEmail);

  const { conditions, askedQuestion, confirmConditions, normalizeConditions } =
    useScenarioAssistant(state => ({
      conditions: state.conditions,
      confirmConditions: state.confirmConditions,
      normalizeConditions: state.normalizeConditions,
      askedQuestion: state.askedQuestion,
    }));
  const [error, setError] = useState('');

  const handleConfirm = () => {
    const selectedConditions = conditions.filter(f => f.active);

    if (selectedConditions.length) {
      if (!validateConditions(selectedConditions)) {
        setError('screener:error.validationError');
        return;
      }

      normalizeConditions();
      confirmConditions();
      handleClose();
    } else {
      setError('screener:error.emptyConditionOptions');
    }
  };

  return (
    <div className={styles.container} data-testid="scenario-assistant-condition-view">
      <div className={styles.styledConditionalWrapper}>
        {askedQuestion && (
          <>
            <h2 className={styles.conditionsTitle} data-testid="new-custom-header">
              {t('screener:modal.conditionEntry')}
            </h2>
            <p className={styles.conditionalDescription}>{askedQuestion}</p>
          </>
        )}
        {error && (
          <Alert
            className={styles.alert}
            type={AlertType.Error}
            variant="inline"
            onClose={() => setError('')}
            message={
              <Trans
                t={t}
                i18nKey={error}
                components={[<ActionLink key="key-0" href={supportEmail} />]}
                values={{
                  email: config.supportEmail,
                }}
              />
            }
          />
        )}
        <h2 className={styles.conditionsTitle} data-testid="new-custom-header">
          {t('screener:conditions')}
        </h2>
        <ScenarioConditionBuilder
          editFilterKey={editFilterKey}
          conditions={conditions}
        />
      </div>
      <footer className={styles.buttonContainer}>
        <Button
          variant="secondary"
          data-testid="edit-button"
          size="large"
          label={t('screener:editTextEntry')}
          onClick={handleEdit}
        />
        <Button
          data-testid="confirm-button"
          variant="primary"
          size="large"
          label={t('common:confirm')}
          onClick={handleConfirm}
        />
      </footer>
    </div>
  );
};
